import {
  ExceptionDetails,
  instanceOfExceptionDetails,
  instanceOfMaintenanceModeExceptionDetails,
  UserCatalogDto,
} from '@/api/generator';
import { api } from '@/api';
import { NotificationMessage } from '@/components/notifications/types';
import { isServerError } from '@/errorHandling';
import { useSystemStore } from '@/stores/systemStore';
import { TFunction } from 'i18next';
import { type Ref, ref } from 'vue';

export function useExceptionDetails(
  t: TFunction,
  exceptionDetails: Ref<ExceptionDetails | undefined> = ref(),
  exceptionNotification: Ref<NotificationMessage | undefined> = ref(),
  loggedInUsers: Ref<UserCatalogDto[] | undefined> = ref(),
) {
  async function handleLoggedInUsersError(): Promise<boolean> {
    useSystemStore().fetchMaintenanceModeInfo();

    const { result, success } = await api.usersApi.getOtherUsersLoggedInCatalog();
    if (!success || !result || result.length === 0) return false;

    loggedInUsers.value = result;
    exceptionNotification.value = {
      type: 'error',
      message: t('Exceptions.ThereAreSomeLoggedInUsersMsg', { count: result.length }),
      closable: false,
    };
    return true;
  }

  function handleExceptionDetailsData(details: unknown) {
    if (!instanceOfExceptionDetails(details)) return;

    exceptionDetails.value = details;
    if (instanceOfMaintenanceModeExceptionDetails(details)) {
      useSystemStore().fetchMaintenanceModeInfo();
    }
  }

  async function handleExceptionDetails(...args: unknown[]) {
    const error = args[0] as Error;

    if (!isServerError(error)) return error;
    if (error.errorResponse?.errorType !== 'PtwUserFriendlyInternalSystemException') return error;

    try {
      const isLoggedInUsersError =
        error.errorResponse?.i18n?.key === 'Exceptions.ThereAreSomeLoggedInUsersMsg';

      if (isLoggedInUsersError) {
        const handled = await handleLoggedInUsersError();
        // Inline notification + user list rendered in dialog; suppress to avoid duplicate global toast (ADO #43312).
        if (handled) return undefined;
      }

      if ('details' in error.errorResponse && error.errorResponse.details) {
        handleExceptionDetailsData(error.errorResponse.details);
      }
    } catch (caughtError: unknown) {
      console.debug(caughtError);
    }

    return error;
  }

  function reset() {
    exceptionDetails.value = undefined;
    exceptionNotification.value = undefined;
    loggedInUsers.value = undefined;
  }

  return {
    handleExceptionDetails,
    reset,
    loggedInUsers,
  };
}
